/**
 * Visit Notes Repository (Driven Adapter)
 *
 * Makes HTTP calls to the unified /visit-notes API endpoints
 */

import type { IAPIRoutes } from "../../../../apiRoutes";
import type {
  IVisitNotesRepository,
  ICreateVisitNoteRequest,
  IUpdateVisitNoteRequest,
  IVisitNote,
  ISummarizeNarrativeRequest,
  ISummarizeNarrativeResponse,
  ISendEfaxRequest,
  ISendEfaxResponse,
  ISidePeekProfile,
  ISidePeekPatientInfo,
  ISafetyNote,
  ICodeStatus,
  CodeStatusValue,
  IPatientContacts,
  IUpdatePatientContactsRequest,
  INextVisitPlan,
  IPatientSummary,
} from "../../../core/ports/driver/VisitNotesService";

const env = import.meta.env;

/** Demographics on GET sidepeek-profile and GET contacts share the same top-level shape */
function patientInfoFromApiEnvelope(data: any): ISidePeekPatientInfo {
  return {
    name: data?.name ?? null,
    dob: data?.dob ?? null,
    gender: data?.gender ?? null,
    weight_kg: data?.weight_kg ?? null,
    age: data?.age ?? null,
    height_cm: data?.height_cm ?? null,
    mr_no: data?.mr_no ?? null,
    rr_baseline_min: data?.rr_baseline_min ?? null,
    rr_baseline_max: data?.rr_baseline_max ?? null,
    patient_id: data?.patient_id ?? null,
    exists: data?.exists ?? null,
  };
}

/** Coerce API phone/email entries to display strings (ignore display-only label fields). */
function contactEntryToString(entry: unknown): string {
  if (entry == null) return "";
  if (typeof entry === "string") return entry;
  if (typeof entry === "object" && entry !== null) {
    const o = entry as Record<string, unknown>;
    for (const k of [
      "value",
      "email",
      "address",
      "phone",
      "phone_number",
    ] as const) {
      const v = o[k];
      if (typeof v === "string") return v;
    }
  }
  return "";
}

function normalizeContactStringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(contactEntryToString);
}

export type IVisitNotesRepositoryType = ReturnType<
  typeof VisitNotesRepositoryFactory
>;

export const VisitNotesRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}): IVisitNotesRepository => {
  const { POST, PATCH, GET, DELETE } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  /**
   * Create a new visit note
   * POST /clinic/{clinic_id}/patient/{patient_id}/visit-notes
   */
  const createVisitNoteAPI = async (
    request: ICreateVisitNoteRequest
  ): Promise<IVisitNote> => {
    const url = APIRoutes.apiCreateVisitNote
      .replace("{clinic_id}", request.clinic_id.toString())
      .replace("{patient_id}", request.patient_id.toString());

    const payload = {
      clinic_id: request.clinic_id,
      patient_id: request.patient_id,
      doc_type: request.doc_type,
      type: request.type,
      notes: request.notes,
      file_link: request.file_link,
      total_time: request.total_time,
      context: request.context,
      ...(request.shortlisted !== undefined && {
        shortlisted: request.shortlisted,
      }),
      ...(request.created_at && {
        created_at: request.created_at,
      }),
    };

    const response = await POST(url, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    return response.data || response;
  };

  /**
   * Update an existing visit note
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}
   */
  const updateVisitNoteAPI = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateVisitNoteRequest
  ): Promise<IVisitNote> => {
    const url = APIRoutes.apiUpdateVisitNote
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString())
      .replace("{note_id}", noteId.toString());

    // Include shortlisted if provided
    const updatePayload = {
      ...request,
      ...(request.shortlisted !== undefined && {
        shortlisted: request.shortlisted,
      }),
    };

    const response = await PATCH(url, updatePayload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    return response.data || response;
  };

  /**
   * Get all visit notes for a clinic
   * GET /clinic/{clinic_id}/visit-notes
   * Supports two mutually exclusive query modes:
   *  - month_offset: 0 = current month, -1 = previous month, etc. (lazy per-month load)
   *  - days_back + days_forward: rolling window (e.g. initial load with days_back=7&days_forward=30)
   */
  const getAllVisitNotesAPI = async (
    clinicId: number,
    params?: {
      monthOffset?: number;
      daysBack?: number;
      daysForward?: number;
      providerEmail?: string;
      providerName?: string;
      includeEmpty?: boolean;
      skipHealthscribe?: boolean;
    }
  ) => {
    const baseUrl = APIRoutes.apiGetAllVisitNotes.replace(
      "{clinic_id}",
      clinicId.toString()
    );

    const queryParts: string[] = [];
    if (params?.monthOffset !== undefined) {
      queryParts.push(`month_offset=${params.monthOffset}`);
    } else {
      if (params?.daysBack !== undefined) {
        queryParts.push(`days_back=${params.daysBack}`);
      }
      if (params?.daysForward !== undefined) {
        queryParts.push(`days_forward=${params.daysForward}`);
      }
    }
    const safeProviderEmail =
      params?.providerEmail && params.providerEmail !== "null"
        ? params.providerEmail
        : undefined;
    if (safeProviderEmail) {
      queryParts.push(
        `provider_email=${encodeURIComponent(safeProviderEmail)}`
      );
    } else if (params?.providerName) {
      queryParts.push(
        `provider_name=${encodeURIComponent(params.providerName)}`
      );
    }
    if (params?.includeEmpty === false) {
      queryParts.push(`include_empty=false`);
    }
    if (params?.skipHealthscribe === true) {
      queryParts.push(`skip_healthscribe=true`);
    }

    const url =
      queryParts.length > 0 ? `${baseUrl}?${queryParts.join("&")}` : baseUrl;

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    return response.data || response;
  };

  /**
   * Get distinct providers for the visit notes provider filter dropdown
   * GET /clinic/{clinic_id}/visit-notes/providers
   */
  const getVisitNoteProvidersAPI = async (clinicId: number) => {
    const url = APIRoutes.apiGetVisitNoteProviders.replace(
      "{clinic_id}",
      clinicId.toString()
    );

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    return response.data || response;
  };

  /**
   * Server-side search for visit notes (transcript search only)
   * GET /clinic/{clinic_id}/visit-notes/search?q=...&limit=...
   * When q is empty: returns all third_party_np_visit notes in date range.
   * When q provided: returns only notes whose transcriptions match.
   */
  const searchVisitNotesAPI = async (
    clinicId: number,
    searchQuery?: string,
    limit?: number
  ) => {
    const baseUrl = APIRoutes.apiSearchVisitNotes.replace(
      "{clinic_id}",
      clinicId.toString()
    );
    const params = new URLSearchParams();
    if (searchQuery?.trim()) params.set("q", searchQuery.trim());
    if (limit != null) params.set("limit", limit.toString());
    const url = params.toString() ? `${baseUrl}?${params}` : baseUrl;

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    return response.data || response;
  };

  /**
   * Get archived visit notes for a clinic
   * GET /clinic/{clinic_id}/visit-notes/archived
   */
  const getArchivedVisitNotesAPI = async (clinicId: number) => {
    const url = APIRoutes.apiGetArchivedVisitNotes.replace(
      "{clinic_id}",
      clinicId.toString()
    );

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    return response.data || response;
  };

  /**
   * Restore an archived visit note
   * PATCH /clinic/{clinic_id}/ipv_notes/{ipv_note_id}/restore
   */
  const restoreArchivedVisitNoteAPI = async (
    clinicId: number,
    ipvNoteId: number
  ) => {
    const url = APIRoutes.apiRestoreArchivedVisitNote
      .replace("{clinic_id}", clinicId.toString())
      .replace("{ipv_note_id}", ipvNoteId.toString());

    const response = await PATCH(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    return response.data || response;
  };

  /**
   * Get a single visit note
   * GET /visit-notes/{note_id}
   */
  const getVisitNoteAPI = async (noteId: number): Promise<IVisitNote> => {
    const url = APIRoutes.apiGetSingleVisitNote.replace(
      "{note_id}",
      noteId.toString()
    );

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    return response.data || response;
  };

  /**
   * Summarize narrative for sharing
   * POST /healthscribe/summarize-narrative
   */
  const summarizeNarrativeAPI = async (
    request: ISummarizeNarrativeRequest
  ): Promise<ISummarizeNarrativeResponse> => {
    const url = APIRoutes.apiSummarizeNarrative;

    const response = await POST(url, request, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    return response.data || response;
  };

  /**
   * Send visit note PDF via eFax
   * POST /clinic/{clinic_id}/visit-notes/efax
   * Uses raw fetch for FormData since APIProvider doesn't handle file uploads properly
   *
   * Supports two methods:
   * - email_gateway (default): Sends via email to RC fax gateway
   * - rc_api: Sends directly via RingCentral Fax API (supports cover page)
   */
  const sendEfaxAPI = async (
    request: ISendEfaxRequest
  ): Promise<ISendEfaxResponse> => {
    try {
      const url = APIRoutes.apiSendVisitNoteEfax.replace(
        "{clinic_id}",
        request.clinicId.toString()
      );

      const formData = new FormData();
      formData.append("file", request.file, request.filename);
      formData.append("efax_email", request.efaxEmail);
      formData.append("patient_name", request.patientName);

      if (request.body) {
        formData.append("body", request.body);
      }

      // Add method parameter (defaults to email_gateway on backend)
      if (request.method) {
        formData.append("method", request.method);
      }

      const authToken = await getAuthenticationToken();

      const response = await fetch(`${env.VITE_API_GATEWAY_URL_V2}${url}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.message || `Failed to send eFax: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("[VisitNotes Repository] Failed to send eFax:", error);
      throw error;
    }
  };

  /**
   * Get Side Peek Profile for a patient
   * GET /clinic/{clinic_id}/patient/{patient_id}/sidepeek-profile
   * Returns both sidepeek_profile and patient_dob
   */
  const getSidePeekProfileAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<{
    sidepeek_profile: ISidePeekProfile | null;
    patient_dob?: string;
    patientInfo: ISidePeekPatientInfo;
  }> => {
    const url = APIRoutes.apiGetSidePeekProfile
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // API may return sidepeek_profile nested plus patient fields on the same object
    const data = response.data || response;
    return {
      sidepeek_profile: data.sidepeek_profile || null,
      patient_dob: data.patient_dob,
      patientInfo: patientInfoFromApiEnvelope(data),
    };
  };

  /**
   * Trigger Side Peek Profile generation for a patient
   * POST /clinic/{clinic_id}/patient/{patient_id}/sidepeek-profile
   */
  const generateSidePeekProfileAPI = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    section?: "diagnoses" | "medications" | "allergies",
    model?: string
  ): Promise<ISidePeekProfile> => {
    const url = APIRoutes.apiGenerateSidePeekProfile
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const payload: { note_id: number; section?: string; model?: string } = {
      note_id: noteId,
    };
    if (section) payload.section = section;
    if (model) payload.model = model;

    const response = await POST(url, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    // Extract sidepeek_profile from the response wrapper
    // API returns: { message, patient_id, sidepeek_profile }
    const data = response.data || response;
    return data.sidepeek_profile || data;
  };

  /**
   * Update Side Peek Profile for a patient (manual edits)
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/sidepeek-profile
   */
  const updateSidePeekProfileAPI = async (
    clinicId: number,
    patientId: number,
    updateData: {
      diagnoses?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        icd10_code?: string | null;
        is_primary?: boolean | null;
        onset_date?: string | null;
      }>;
      medications?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        status?: string | null;
      }>;
      allergies?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        icd10_code?: string | null;
        status?: string | null;
        allergen?: string | null;
        reaction?: string | null;
        severity?: string | null;
        onset_date?: string | null;
        is_primary?: boolean | null;
      }>;
    }
  ): Promise<ISidePeekProfile> => {
    const url = APIRoutes.apiUpdateSidePeekProfile
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await PATCH(url, updateData, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    // Extract sidepeek_profile from the response wrapper
    // API returns: { message, patient_id, sidepeek_profile }
    const data = response.data || response;
    return data.sidepeek_profile || data;
  };

  /**
   * Get Safety Note for a patient
   * GET /clinic/{clinic_id}/patient/{patient_id}/safety-note
   */
  const getSafetyNoteAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<ISafetyNote> => {
    const url = APIRoutes.apiGetSafetyNote
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // Extract safety_note from the response wrapper
    // API returns: { patient_id, safety_note, exists }
    const data = response.data || response;
    return data.safety_note || { content: "" };
  };

  /**
   * Update Safety Note for a patient
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/safety-note
   */
  const updateSafetyNoteAPI = async (
    clinicId: number,
    patientId: number,
    content: string
  ): Promise<ISafetyNote> => {
    const url = APIRoutes.apiUpdateSafetyNote
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await PATCH(
      url,
      { content },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // Extract safety_note from the response wrapper
    // API returns: { message, patient_id, safety_note }
    const data = response.data || response;
    return data.safety_note || { content };
  };

  /**
   * Get Code Status for a patient
   * GET /clinic/{clinic_id}/patient/{patient_id}/code-status
   */
  const getCodeStatusAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<ICodeStatus> => {
    const url = APIRoutes.apiGetCodeStatus
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // Extract code_status from the response wrapper
    // API returns: { patient_id, code_status, exists }
    const data = response.data || response;
    return data.code_status || { status: "NOT_SPECIFIED" };
  };

  /**
   * Update Code Status for a patient
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/code-status
   */
  const updateCodeStatusAPI = async (
    clinicId: number,
    patientId: number,
    status: CodeStatusValue,
    otherStatus?: string | null
  ): Promise<ICodeStatus> => {
    const url = APIRoutes.apiUpdateCodeStatus
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await PATCH(
      url,
      { status, other_status: otherStatus },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // Extract code_status from the response wrapper
    // API returns: { message, patient_id, code_status }
    const data = response.data || response;
    return data.code_status || { status };
  };

  /**
   * Get Patient Contacts
   * GET /clinic/{clinic_id}/patient/{patient_id}/contacts
   */
  const getPatientContactsAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<{
    contacts: IPatientContacts;
    patientInfo: ISidePeekPatientInfo;
  }> => {
    const url = APIRoutes.apiGetPatientContacts
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // API returns demographics at top level; contacts may be null
    const data = response.data || response;
    const raw = data.contacts;
    const phones = normalizeContactStringList(raw?.phones);
    const emails = normalizeContactStringList(raw?.emails);
    return {
      contacts: { phones, emails },
      patientInfo: patientInfoFromApiEnvelope(data),
    };
  };

  /**
   * Add or remove a patient contact
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/contacts
   */
  const updatePatientContactsAPI = async (
    clinicId: number,
    patientId: number,
    request: IUpdatePatientContactsRequest
  ): Promise<IPatientContacts> => {
    const url = APIRoutes.apiUpdatePatientContacts
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await PATCH(url, request, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    // Extract contacts from the response wrapper
    const data = response.data || response;
    const c = data.contacts || { phones: [], emails: [] };
    return {
      phones: normalizeContactStringList(c.phones),
      emails: normalizeContactStringList(c.emails),
    };
  };

  /**
   * Get Next Visit Plan for a patient
   * GET /clinic/{clinic_id}/patient/{patient_id}/next-visit-plan
   */
  const getNextVisitPlanAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<INextVisitPlan | null> => {
    const url = APIRoutes.apiGetNextVisitPlan
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    const data = response.data || response;
    return data.next_visit_plan || null;
  };

  /**
   * Update Next Visit Plan for a patient
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/next-visit-plan
   */
  const updateNextVisitPlanAPI = async (
    clinicId: number,
    patientId: number,
    plan: INextVisitPlan["plan"]
  ): Promise<INextVisitPlan> => {
    const url = APIRoutes.apiUpdateNextVisitPlan
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await PATCH(
      url,
      { plan },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    const data = response.data || response;
    return data.next_visit_plan || { plan };
  };

  /**
   * GET /clinic/{clinic_id}/patient/{patient_id}/patient-summary
   * Returns the stored patient summary, or exists:false when none generated yet.
   */
  const getPatientSummaryAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<IPatientSummary> => {
    const url = APIRoutes.apiGetSidePeekPatientSummary
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );

    const data = response.data || response;
    const rawSummary = data.patient_summary ?? null;
    const patient_summary =
      rawSummary && typeof rawSummary === "object" && !Array.isArray(rawSummary)
        ? {
            background: Array.isArray(rawSummary.background)
              ? rawSummary.background
              : [],
            recommendations: Array.isArray(rawSummary.recommendations)
              ? rawSummary.recommendations
              : [],
          }
        : null;
    const rawRisk = data.clinical_risk ?? null;
    const clinical_risk =
      rawRisk && typeof rawRisk === "object" && !Array.isArray(rawRisk)
        ? {
            score: rawRisk.score ?? 0,
            label: rawRisk.label ?? "",
            rationale: rawRisk.rationale ?? "",
            visit_notes_used: rawRisk.visit_notes_used ?? 0,
            diagnoses_used: rawRisk.diagnoses_used ?? 0,
            cached: rawRisk.cached ?? false,
            generated_at: rawRisk.generated_at ?? "",
          }
        : null;
    return {
      patient_id: data.patient_id ?? patientId,
      patient_summary,
      has_visit_context: data.has_visit_context ?? false,
      generated_at: data.generated_at ?? null,
      generated_by: data.generated_by ?? null,
      exists: data.exists ?? false,
      clinical_risk,
    };
  };

  /**
   * POST /clinic/{clinic_id}/patient/{patient_id}/patient-summary
   * Triggers on-demand generation of the patient summary.
   */
  const generatePatientSummaryAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<IPatientSummary> => {
    const url = APIRoutes.apiGenerateSidePeekPatientSummary
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await POST(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );

    const data = response.data || response;
    const rawGenSummary = data.patient_summary ?? null;
    const patient_summary_gen =
      rawGenSummary &&
      typeof rawGenSummary === "object" &&
      !Array.isArray(rawGenSummary)
        ? {
            background: Array.isArray(rawGenSummary.background)
              ? rawGenSummary.background
              : [],
            recommendations: Array.isArray(rawGenSummary.recommendations)
              ? rawGenSummary.recommendations
              : [],
          }
        : null;
    const rawRiskGen = data.clinical_risk ?? null;
    const clinical_risk_gen =
      rawRiskGen && typeof rawRiskGen === "object" && !Array.isArray(rawRiskGen)
        ? {
            score: rawRiskGen.score ?? 0,
            label: rawRiskGen.label ?? "",
            rationale: rawRiskGen.rationale ?? "",
            visit_notes_used: rawRiskGen.visit_notes_used ?? 0,
            diagnoses_used: rawRiskGen.diagnoses_used ?? 0,
            cached: rawRiskGen.cached ?? false,
            generated_at: rawRiskGen.generated_at ?? "",
          }
        : null;
    return {
      patient_id: data.patient_id ?? patientId,
      patient_summary: patient_summary_gen,
      has_visit_context: data.has_visit_context ?? false,
      generated_at: data.generated_at ?? null,
      generated_by: data.generated_by ?? null,
      exists: !!(
        patient_summary_gen?.background?.length ||
        patient_summary_gen?.recommendations?.length
      ),
      clinical_risk: clinical_risk_gen,
    };
  };

  /**
   * GET /healthscribe/note/{note_id}/llm-output?clinic_id=
   */
  const getNoteLlmOutputAPI = async (noteId: number, clinicId: number) => {
    const url =
      APIRoutes.apiGetNoteLlmOutput.replace("{note_id}", noteId.toString()) +
      `?clinic_id=${clinicId}`;

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    const data = response.data || response;
    return {
      latest_llm_output: data.latest_llm_output ?? null,
      updated_at: data.updated_at ?? null,
    };
  };

  /**
   * DELETE /healthscribe/note/{note_id}/manual-edits-history?clinic_id=
   */
  const clearManualEditsHistoryAPI = async (
    noteId: number,
    clinicId: number
  ): Promise<void> => {
    const url =
      APIRoutes.apiDeleteNoteManualEditsHistory.replace(
        "{note_id}",
        noteId.toString()
      ) + `?clinic_id=${clinicId}`;

    await DELETE(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  return {
    createVisitNoteAPI,
    updateVisitNoteAPI,
    getAllVisitNotesAPI,
    getVisitNoteProvidersAPI,
    searchVisitNotesAPI,
    getArchivedVisitNotesAPI,
    restoreArchivedVisitNoteAPI,
    getVisitNoteAPI,
    summarizeNarrativeAPI,
    sendEfaxAPI,
    getSidePeekProfileAPI,
    generateSidePeekProfileAPI,
    updateSidePeekProfileAPI,
    getSafetyNoteAPI,
    updateSafetyNoteAPI,
    getCodeStatusAPI,
    updateCodeStatusAPI,
    getPatientContactsAPI,
    updatePatientContactsAPI,
    getNextVisitPlanAPI,
    updateNextVisitPlanAPI,
    getPatientSummaryAPI,
    generatePatientSummaryAPI,
    getNoteLlmOutputAPI,
    clearManualEditsHistoryAPI,
  };
};
